import { useEffect, useMemo, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'src/common/components';
import { DualListBox } from 'src/common/components/DualListBox';
import { useQuery } from 'src/common/hooks/useQuery';
import { getCampaignDetails, saveCampaignDetails, updateCampaignDetails } from 'src/tcr/api';

export const CampaignDetailsManage = (): JSX.Element => {
  const navigate = useNavigate();
  const [initialAccounts, setInitialAccounts] = useState<string[]>([]);
  const { id } = useParams();
  const [selectedAccounts, setSelectedAccounts] = useState<any>([]);
  const [queryAvailable, setQueryAvailable] = useState('');
  const { data } = useQuery(
    `/account_admin?page=1&search=${queryAvailable}&ordering=name&basic_info=true`
  );
  const accounts = (data as { results: Array<any> })?.results || [];

  const {
    register,
    getValues,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm({ mode: 'onChange' });

  const hasAccountChanges = useMemo(() => {
    return (
      selectedAccounts.length !== initialAccounts.length ||
      !selectedAccounts.every((item: any) => initialAccounts.includes(item.id))
    );
  }, [selectedAccounts, initialAccounts]);

  useEffect(() => {
    if (id) {
      const getData = async (): Promise<any> => {
        const campaign: any = await getCampaignDetails(id);
        if (Array.isArray(campaign?.accounts)) {
          setSelectedAccounts(campaign?.accounts);
          setInitialAccounts(campaign?.accounts.map((selected: any) => selected.id as string));
        }
        reset({
          tcr_description: campaign?.tcr_description,
          name: campaign?.name,
          tcr_message_flow: campaign?.tcr_message_flow,
        });
      };
      getData();
    }
  }, [id, reset]);

  const title = id ? 'Edit campaign details' : 'Create new campaign details';

  const handleSave = async (): Promise<void> => {
    try {
      const values = getValues();
      values.accounts = selectedAccounts.map((selected: any) => selected.id as string);
      if (!id) await saveCampaignDetails(values);
      else await updateCampaignDetails({ ...values, id });
      Toast.success('Campaign details has been saved successfully.');
      navigate('/app/tcr/campaign_details');
    } catch (error) {
      Toast.error('Error occurred while save campaign details.');
    }
  };
  return (
    <div className="sm:px-6 lg:px-8 flex flex-col h-full px-4 py-8 overflow-auto">
      <h2 className="items-center">
        {title}
        <p className="mt-2 text-xs">
          You can see{' '}
          <a
            target="_blank"
            className="text-blue-1.0 underline"
            href="https://www.notion.so/brainhi/TCR-Campaign-Templates-bc85f27880474eae92280fd712db0c6f"
          >
            Campaign Details Templates
          </a>
        </p>
      </h2>
      <div className="flex flex-col flex-1 my-4">
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0 items-center">
          <div className="flex-col">
            <div className="flex gap-1">
              <span className="text-red-1.0 font-semibold">(*)</span>
              <h4 className="mb-2 font-semibold">Name:</h4>
            </div>
            <p className="text-xs">Field for the unique name of the campaign.</p>
          </div>
          <div className="flex-col">
            <input
              placeholder="TCR Campaign Details for <insert brand name>"
              className={`input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out`}
              {...register('name', {
                required: 'Name is required',
              })}
            />
            {errors?.name && <p className="error-text">{errors?.name?.message}</p>}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0 items-center">
          <div className="flex-col">
            <div className="flex gap-1">
              <span className="text-red-1.0 font-semibold">(*)</span>
              <h4 className="mb-2 font-semibold">Message flow:</h4>
            </div>
            <p className="mb-1 text-xs">{'Field for campaign descriptive details.'}</p>
            <p className="text-xs">
              {
                'You can use {{NAME}} var for dynamic message, this value will be replaced by legal name of the legal entity assoc to accounts'
              }
            </p>
          </div>
          <div className="flex-col">
            <textarea
              rows={4}
              className={`input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out`}
              {...register('tcr_message_flow', {
                required: 'Message flow is required',
              })}
            />
            {errors?.tcr_message_flow && (
              <p className="error-text">{errors?.tcr_message_flow?.message}</p>
            )}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0 items-center">
          <div className="flex-col">
            <div className="flex gap-1">
              <span className="text-red-1.0 font-semibold">(*)</span>
              <h4 className="mb-2 font-semibold">Description:</h4>
            </div>
            <p className="mb-1 text-xs">
              {"Field that describes how the customer's consent for the subscription is obtained."}
            </p>
            <p className="text-xs">
              {
                'You can use {{NAME}} var for dynamic message, this value will be replaced by legal name of the legal entity assoc to accounts'
              }
            </p>
          </div>
          <div className="flex-col">
            <textarea
              rows={4}
              className={`input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out`}
              {...register('tcr_description', {
                required: 'Description is required',
              })}
            />
            {errors?.tcr_description && (
              <p className="error-text">{errors?.tcr_description?.message}</p>
            )}
          </div>
        </div>
        <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 px-4 py-5 border-b-2 border-white-1.0 items-center flex-1">
          <div className="flex-col">
            <h4 className="mb-2 font-semibold">Accounts:</h4>
            <p className="text-xs">Associate or disassociate accounts with the campaign details</p>
          </div>
          <div className="flex-col h-full">
            <DualListBox
              className="h-full"
              options={accounts}
              onChange={(newValue) => setSelectedAccounts(newValue)}
              selected={selectedAccounts}
              onSearch={setQueryAvailable}
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            disabled={!(isValid && (hasAccountChanges || isDirty))}
            className="sm:flex btn btn-secondary hidden ml-4"
            type="button"
            onClick={handleSave}
          >
            <p className="text-white-0.0 font-semibold">{`${
              id ? 'Update ' : 'Create'
            } campaign details`}</p>
          </button>
        </div>
      </div>
    </div>
  );
};
